// src/components/bot/BotPreviewButton.tsx
// 🎬 BOT PREVIEW INTEGRATION - Add preview functionality to bot form
// 🚀 Seamlessly integrate the preview modal into the bot creation flow

import React, { useState } from 'react';
import BotFlowPreview from './BotFlowPreview';
import styles from './BotPreviewButton.module.css';
import logger from '../../../utils/logger';

/**
 * 📝 INTERFACE DEFINITIONS
 * Props for the preview button component
 */
interface Question {
  id: string;
  question: string;
  type: string;
  required: boolean;
  interactive?: any;
}

interface BotPreviewButtonProps {
  // Bot form data
  botName: string;
  keywords: string[];
  questions: Question[];
  selectedTemplate?: any;
  responseType: 'template' | 'questionnaire';
  
  // UI configuration
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  className?: string;
  
  // Event handlers
  onPreviewOpen?: () => void;
  onPreviewClose?: () => void;
}

/**
 * 🎬 BOT PREVIEW BUTTON COMPONENT
 * Trigger button that opens the full bot flow preview
 */
const BotPreviewButton: React.FC<BotPreviewButtonProps> = ({
  botName,
  keywords,
  questions,
  selectedTemplate,
  responseType,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  className = '',
  onPreviewOpen,
  onPreviewClose
}) => {
  logger.log('🎬 BotPreviewButton: Component initialized with:', {
    botName,
    keywordsCount: keywords.length,
    questionsCount: questions.length,
    responseType,
    variant,
    size,
    disabled
  });

  // 📊 STATE MANAGEMENT
  const [showPreview, setShowPreview] = useState(false);

  /**
   * 🎯 VALIDATION LOGIC
   * Check if preview can be shown based on form state
   */
  const canShowPreview = (): boolean => {
    logger.log('🔍 BotPreviewButton: Validating preview readiness...');
    
    // Basic validation
    if (!botName || botName.trim().length === 0) {
      logger.log('❌ BotPreviewButton: Bot name is required');
      return false;
    }

    if (keywords.length === 0) {
      logger.log('❌ BotPreviewButton: At least one keyword is required');
      return false;
    }

    // Questionnaire mode validation
    if (responseType === 'questionnaire') {
      if (questions.length === 0) {
        logger.log('❌ BotPreviewButton: Questionnaire mode requires questions');
        return false;
      }
    }

    // Template mode validation
    if (responseType === 'template') {
      if (!selectedTemplate) {
        logger.log('❌ BotPreviewButton: Template mode requires a selected template');
        return false;
      }
    }

    logger.log('✅ BotPreviewButton: Preview validation passed');
    return true;
  };

  /**
   * 🎬 HANDLE PREVIEW OPEN
   * Open the preview modal with validation
   */
  const handlePreviewOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling
    
    logger.log('🎬 BotPreviewButton: Preview button clicked');

    if (!canShowPreview()) {
      logger.log('❌ BotPreviewButton: Cannot show preview - validation failed');
      return;
    }

    logger.log('✅ BotPreviewButton: Opening preview modal');
    setShowPreview(true);
    onPreviewOpen?.();
  };

  /**
   * 🔄 HANDLE PREVIEW CLOSE
   * Close the preview modal
   */
  const handlePreviewClose = () => {
    logger.log('🔄 BotPreviewButton: Closing preview modal');
    setShowPreview(false);
    onPreviewClose?.();
  };

  /**
   * 🎨 GENERATE BUTTON CLASS NAMES
   * Create CSS classes based on props
   */
  const getButtonClasses = (): string => {
    const baseClasses = [styles.previewButton];
    
    // Add variant class
    baseClasses.push(styles[`variant${variant.charAt(0).toUpperCase() + variant.slice(1)}`]);
    
    // Add size class
    baseClasses.push(styles[`size${size.charAt(0).toUpperCase() + size.slice(1)}`]);
    
    // Add state classes
    if (disabled || !canShowPreview()) {
      baseClasses.push(styles.disabled);
    }
    
    // Add custom className
    if (className) {
      baseClasses.push(className);
    }

    return baseClasses.join(' ');
  };

  /**
   * 🎯 GET BUTTON CONTENT
   * Generate button text and icon based on validation state
   */
  const getButtonContent = () => {
    const isReady = canShowPreview();
    
    if (!botName || botName.trim().length === 0) {
      return {
        icon: '📝',
        text: 'Add Bot Name',
        tooltip: 'Bot name is required to preview'
      };
    }
    
    if (keywords.length === 0) {
      return {
        icon: '🔑',
        text: 'Add Keywords',
        tooltip: 'Keywords are required to preview'
      };
    }
    
    if (responseType === 'questionnaire' && questions.length === 0) {
      return {
        icon: '📋',
        text: 'Add Questions',
        tooltip: 'Questions are required for questionnaire mode'
      };
    }
    
    if (responseType === 'template' && !selectedTemplate) {
      return {
        icon: '📄',
        text: 'Select Template',
        tooltip: 'Template is required for template mode'
      };
    }
    
    if (isReady) {
      return {
        icon: '🎬',
        text: 'Preview Bot Flow',
        tooltip: 'See how your bot will work in WhatsApp'
      };
    }
    
    return {
      icon: '⚠️',
      text: 'Preview Not Ready',
      tooltip: 'Complete the form to enable preview'
    };
  };

  const buttonContent = getButtonContent();
  const isPreviewReady = canShowPreview();

  logger.log('🎨 BotPreviewButton: Rendering button with state:', {
    isPreviewReady,
    buttonText: buttonContent.text,
    showPreview
  });

  return (
    <>
      {/* 🎯 PREVIEW TRIGGER BUTTON */}
      <button
        className={getButtonClasses()}
        onClick={handlePreviewOpen}
        disabled={disabled || !isPreviewReady}
        title={buttonContent.tooltip}
        type="button"
      >
        <span className={styles.buttonIcon}>
          {buttonContent.icon}
        </span>
        <span className={styles.buttonText}>
          {buttonContent.text}
        </span>
        {isPreviewReady && (
          <span className={styles.buttonBadge}>
            Ready
          </span>
        )}
      </button>

      {/* 🎬 PREVIEW MODAL */}
      {showPreview && (
        <BotFlowPreview
          botName={botName}
          keywords={keywords}
          questions={questions}
          selectedTemplate={selectedTemplate}
          responseType={responseType}
          onClose={handlePreviewClose}
        />
      )}
    </>
  );
};

export default BotPreviewButton;